import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { Tooltip } from "@/components/Tooltip";
import type { PlainWorkflowExecution } from "@/queries/automation-queries";
import { workflowExecutionPhaseLabel } from "@/utils/automations";
import { IconCircleCheck as CentralIconCircleCheck } from "central-icons/IconCircleCheck";
import IconCircleDashed from "central-icons/IconCircleDashed";
import { WorkflowExecutionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { FC } from "react";
import { withGitpodSize } from "@/assets/icons/utils";
import { cn } from "@/components/podkit/lib/cn";

const IconCircleCheck = withGitpodSize(CentralIconCircleCheck);
const IconCircleDashedWithSize = withGitpodSize(IconCircleDashed);

export const WorkflowExecutionStatusIcon: FC<{ execution?: PlainWorkflowExecution }> = ({ execution }) => {
    // No execution - show empty dashed circle
    if (!execution) {
        return <IconCircleDashedWithSize size="sm" className="shrink-0 text-border-base" />;
    }

    const phase = execution.status?.phase;

    const phaseConfig = {
        [WorkflowExecutionPhase.COMPLETED]: {
            icon: IconCircleCheck,
            className: "text-content-success",
        },
        [WorkflowExecutionPhase.RUNNING]: {
            icon: IconSpinner,
            className: "animate-spin text-content-muted",
        },
        [WorkflowExecutionPhase.PENDING]: {
            icon: IconSpinner,
            className: "animate-spin",
        },
        [WorkflowExecutionPhase.STOPPED]: {
            icon: IconWarningCircle,
            className: "text-content-muted",
        },
        [WorkflowExecutionPhase.STOPPING]: {
            icon: IconWarningCircle,
            className: "text-content-muted",
        },
        [WorkflowExecutionPhase.DELETED]: {
            icon: IconTrash,
            className: "text-content-destructive",
        },
        [WorkflowExecutionPhase.DELETING]: {
            icon: IconSpinner,
            className: "text-content-muted",
        },
    };

    if (!phase || !phaseConfig[phase]) {
        return null;
    }

    const label = workflowExecutionPhaseLabel(execution);
    const { icon: Icon, className } = phaseConfig[phase];

    return (
        <Tooltip content={label} usePortal>
            <div>
                <span className="sr-only">{label}</span>
                <Icon size="sm" className={cn("shrink-0", className)} aria-hidden />
            </div>
        </Tooltip>
    );
};
